import axios from "axios";
import {
  ReservableAsset,
  Reservation,
  CreateReservationRequest,
  CreateRecurringReservationRequest,
  ReservationActionDto,
  AssetStatistics,
  ApiResponse,
  ReservationStatus,
  CreateAssetRequest,
} from "../types/reservation";

const API_BASE_URL = "/ofc/reservation";

// Configure axios defaults
axios.defaults.withCredentials = true;

class ReservationApiService {
  // ========== Asset APIs (Public) ==========

  async getActiveAssets(): Promise<ReservableAsset[]> {
    const response = await axios.get<ApiResponse<ReservableAsset[]>>(
      `${API_BASE_URL}/assets`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch assets");
    }
    return response.data.data;
  }

  async getAssetById(id: number): Promise<ReservableAsset> {
    const response = await axios.get<ApiResponse<ReservableAsset>>(
      `${API_BASE_URL}/assets/${id}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch asset");
    }
    return response.data.data;
  }

  async searchAssets(term: string): Promise<ReservableAsset[]> {
    const response = await axios.get<ApiResponse<ReservableAsset[]>>(
      `${API_BASE_URL}/assets/search`,
      { params: { term } },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to search assets");
    }
    return response.data.data;
  }

  async getAssetsByType(type: string): Promise<ReservableAsset[]> {
    const response = await axios.get<ApiResponse<ReservableAsset[]>>(
      `${API_BASE_URL}/assets/type/${encodeURIComponent(type)}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch assets by type");
    }
    return response.data.data;
  }

  async getAssetsByCategory(category: string): Promise<ReservableAsset[]> {
    const response = await axios.get<ApiResponse<ReservableAsset[]>>(
      `${API_BASE_URL}/assets/category/${encodeURIComponent(category)}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch assets by category");
    }
    return response.data.data;
  }

  async getDistinctTypes(): Promise<string[]> {
    const response = await axios.get<ApiResponse<string[]>>(
      `${API_BASE_URL}/assets/types`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch distinct types");
    }
    return response.data.data;
  }

  async getDistinctCategories(): Promise<string[]> {
    const response = await axios.get<ApiResponse<string[]>>(
      `${API_BASE_URL}/assets/categories`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch distinct categories");
    }
    return response.data.data;
  }

  // ========== Asset APIs (Admin) ==========

  async createAsset(asset: Partial<ReservableAsset>): Promise<ReservableAsset> {
    const response = await axios.post<ApiResponse<ReservableAsset>>(
      `${API_BASE_URL}/assets`,
      asset,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to create asset");
    }
    return response.data.data;
  }

  async updateAsset(
    id: number,
    asset: Partial<ReservableAsset>,
  ): Promise<ReservableAsset> {
    const response = await axios.put<ApiResponse<ReservableAsset>>(
      `${API_BASE_URL}/assets/${id}`,
      asset,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to update asset");
    }
    return response.data.data;
  }

  async activateAsset(id: number): Promise<ReservableAsset> {
    const response = await axios.put<ApiResponse<ReservableAsset>>(
      `${API_BASE_URL}/assets/${id}/activate`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to activate asset");
    }
    return response.data.data;
  }

  async deactivateAsset(id: number): Promise<ReservableAsset> {
    const response = await axios.put<ApiResponse<ReservableAsset>>(
      `${API_BASE_URL}/assets/${id}/deactivate`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to deactivate asset");
    }
    return response.data.data;
  }

  async deleteAsset(id: number): Promise<void> {
    const response = await axios.delete<ApiResponse<string>>(
      `${API_BASE_URL}/assets/${id}`,
    );
    if (!response.data.success) {
      throw new Error("Failed to delete asset");
    }
  }

  async getAssetStatistics(): Promise<AssetStatistics> {
    const response = await axios.get<ApiResponse<AssetStatistics>>(
      `${API_BASE_URL}/assets/statistics`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch asset statistics");
    }
    return response.data.data;
  }

  // ========== Reservation APIs (Employee) ==========

  async createReservation(
    request: CreateReservationRequest,
  ): Promise<Reservation[]> {
    try {
      const response = await axios.post<ApiResponse<Reservation[]>>(
        `${API_BASE_URL}/reservations`,
        request,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.message || "Failed to create reservation",
        );
      }
      return response.data.data;
    } catch (error: any) {
      // Extract error message from backend response
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create reservation";
      throw new Error(message);
    }
  }

  async createRecurringReservation(
    request: CreateRecurringReservationRequest,
  ): Promise<Reservation[]> {
    try {
      const response = await axios.post<ApiResponse<Reservation[]>>(
        `${API_BASE_URL}/reservations/recurring`,
        request,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.message || "Failed to create recurring reservation",
        );
      }
      return response.data.data;
    } catch (error: any) {
      // Extract error message from backend response
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create recurring reservation";
      throw new Error(message);
    }
  }

  async getMyReservations(timezone?: string): Promise<Reservation[]> {
    const params = {
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/me`,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch my reservations");
    }
    return response.data.data;
  }

  async getReservationById(id: number): Promise<Reservation> {
    const response = await axios.get<ApiResponse<Reservation>>(
      `${API_BASE_URL}/reservations/${id}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch reservation");
    }
    return response.data.data;
  }

  async getReservationsByAsset(
    assetId: number,
    startDate?: string,
    endDate?: string,
    timezone?: string,
  ): Promise<Reservation[]> {
    const params: any = {
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/asset/${assetId}`,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch reservations by asset");
    }
    return response.data.data;
  }

  async getUpcomingReservations(timezone?: string): Promise<Reservation[]> {
    const params = {
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/upcoming`,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch upcoming reservations");
    }
    return response.data.data;
  }

  async cancelReservation(id: number, reason?: string): Promise<Reservation> {
    const params: any = {};
    if (reason) params.reason = reason;

    const response = await axios.put<ApiResponse<Reservation>>(
      `${API_BASE_URL}/reservations/${id}/cancel`,
      null,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to cancel reservation");
    }
    return response.data.data;
  }

  async cancelRecurringReservations(
    groupId: string,
    reason?: string,
  ): Promise<Reservation[]> {
    const params: any = {};
    if (reason) params.reason = reason;

    const response = await axios.put<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/recurring/${groupId}/cancel`,
      null,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to cancel recurring reservations");
    }
    return response.data.data;
  }

  async cancelContinuousReservations(
    groupId: string,
    reason?: string,
  ): Promise<Reservation[]> {
    const params: any = {};
    if (reason) params.reason = reason;

    const response = await axios.put<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/continuous/${groupId}/cancel`,
      null,
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to cancel continuous reservations");
    }
    return response.data.data;
  }

  // ========== Reservation APIs (Admin/Approver) ==========

  async getPendingApprovals(): Promise<Reservation[]> {
    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/pending`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch pending approvals");
    }
    return response.data.data;
  }

  async getPendingApprovalsCount(): Promise<number> {
    const response = await axios.get<ApiResponse<number>>(
      `${API_BASE_URL}/reservations/pending/count`,
    );
    if (!response.data.success || response.data.data === undefined) {
      throw new Error("Failed to fetch pending approvals count");
    }
    return response.data.data;
  }

  async approveReservation(id: number): Promise<Reservation> {
    const response = await axios.put<ApiResponse<Reservation>>(
      `${API_BASE_URL}/reservations/${id}/approve`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to approve reservation");
    }
    return response.data.data;
  }

  async rejectReservation(id: number, reason: string): Promise<Reservation> {
    const response = await axios.put<ApiResponse<Reservation>>(
      `${API_BASE_URL}/reservations/${id}/reject`,
      { action: "REJECT", reason },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to reject reservation");
    }
    return response.data.data;
  }

  async getReservationsByStatus(
    status: ReservationStatus,
  ): Promise<Reservation[]> {
    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/status/${status}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch reservations by status");
    }
    return response.data.data;
  }

  async getReservationsByDateRange(
    startDate: string,
    endDate: string,
    timezone: string,
  ): Promise<Reservation[]> {
    const response = await axios.get<ApiResponse<Reservation[]>>(
      `${API_BASE_URL}/reservations/date-range`,
      { params: { startDate, endDate, timezone } },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error("Failed to fetch reservations by date range");
    }
    return response.data.data;
  }
}

export default new ReservationApiService();
